import React, { useState, useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  XIcon,
  UserIcon,
  HelpCircleIcon,
  LogOutIcon,
  LogInIcon,
  Loader2,
  Store,
  Mail,
  Phone,
  Bike,
  ClipboardList,
  Activity,
} from 'lucide-react';
import { markAttendance } from '../services/attendance.service';
import { getDeliveryProfile } from '../services/profile.service';
import { getUserLocation } from '../utils/locationUtils';
import {
  STORE_CHECK_IN_RADIUS_METERS,
  validateStoreCheckInGeofence,
} from '../utils/storeCheckInGeofence';
import {
  readPartnerIsAvailableFromPayload,
  readPartnerIsCheckedInTodayFromPayload,
} from '../utils/partnerAvailability';
import {
  persistAttendanceAction,
  resolvePartnerCheckedInForUi,
  subscribeMidnightAttendanceReset,
} from '../utils/partnerAttendanceUi';
import { useToast } from '../contexts/ToastContext';
import Spinner from './common/Spinner';
import { MAIN_SPINNER_SIZE } from './common/MainContentLoader';
import { callDeliveryManagerPhone, getDeliveryManagerPhone } from '../utils/deliveryProfileDisplay';

interface SideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
  onLogout: () => void | Promise<void>;
  onPartnerAvailabilityChange?: (isAvailable: boolean | null) => void;
}

function MetaRow({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
        <Icon size={18} className="text-gray-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <div className="text-sm font-medium text-gray-900 break-words">{value || '—'}</div>
      </div>
    </div>
  );
}

const SideDrawer: React.FC<SideDrawerProps> = ({
  isOpen,
  onClose,
  onLogout,
  onPartnerAvailabilityChange,
}) => {
  const [profile, setProfile] = useState<any>(null);
  const [isCheckedIn, setIsCheckedIn] = useState<boolean | null>(null);
  const [isMarking, setIsMarking] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const { showSuccess, showError } = useToast();

  const managerPhone =
    (profile?.deliveryManagerPhone as string | undefined) ||
    getDeliveryManagerPhone(profile as Record<string, unknown> | undefined);

  const fetchProfile = async (opts?: { showLoading?: boolean }): Promise<boolean | null> => {
    const showLoading = opts?.showLoading !== false;
    if (showLoading) setIsProfileLoading(true);
    try {
      const res = await getDeliveryProfile();
      const data = (res as any)?.data != null ? (res as any).data : res;
      const raw = data as Record<string, unknown>;
      const p = {
        ...data,
        employeeName:
          data?.full_name ||
          data?.employeeName ||
          data?.user?.full_name ||
          data?.name ||
          'Not Available',
        deliveryManagerPhone: getDeliveryManagerPhone(raw),
      };
      setProfile(p);
      // Prefer the server-derived day-aware flag (Plan B). Fall back to the
      // legacy `is_available`-based resolver if the field is missing.
      const serverCheckedInToday = readPartnerIsCheckedInTodayFromPayload(p);
      const apiAvailable = readPartnerIsAvailableFromPayload(p);
      const uiCheckedIn =
        serverCheckedInToday !== null
          ? serverCheckedInToday
          : resolvePartnerCheckedInForUi(apiAvailable);
      setIsCheckedIn(uiCheckedIn);
      onPartnerAvailabilityChange?.(uiCheckedIn);
      localStorage.setItem('profile', JSON.stringify(p));
      if (serverCheckedInToday !== null) return uiCheckedIn;
      return apiAvailable === null ? null : uiCheckedIn;
    } catch {
      const stored = localStorage.getItem('profile');
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as Record<string, unknown>;
          const p = {
            ...parsed,
            deliveryManagerPhone: getDeliveryManagerPhone(parsed),
          };
          setProfile(p);
          const serverToday = readPartnerIsCheckedInTodayFromPayload(p);
          const apiAv = readPartnerIsAvailableFromPayload(p);
          const uiIn =
            serverToday !== null
              ? serverToday
              : resolvePartnerCheckedInForUi(apiAv);
          setIsCheckedIn(uiIn);
          onPartnerAvailabilityChange?.(uiIn);
          return uiIn;
        } catch {
          setProfile(null);
          setIsCheckedIn(null);
          onPartnerAvailabilityChange?.(null);
          return null;
        }
      } else {
        setProfile(null);
        setIsCheckedIn(null);
        onPartnerAvailabilityChange?.(null);
        return null;
      }
    } finally {
      if (showLoading) setIsProfileLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) void fetchProfile({ showLoading: true });
  }, [isOpen]);

  useEffect(() => {
    return subscribeMidnightAttendanceReset(() => {
      setIsCheckedIn(false);
      onPartnerAvailabilityChange?.(false);
    });
  }, [onPartnerAvailabilityChange]);

  const handleCallManager = () => {
    callDeliveryManagerPhone(showError);
  };

  const handleCheckInOut = async () => {
    if (isMarking) return;
    setIsMarking(true);
    try {
      let latitude: number | undefined;
      let longitude: number | undefined;
      const checkingIn = isCheckedIn !== true;

      if (checkingIn) {
        let loc: { lat: number; lng: number };
        try {
          loc = await getUserLocation();
        } catch (locErr: unknown) {
          const msg =
            locErr instanceof Error
              ? locErr.message
              : "Location is required to check in. Please enable GPS.";
          showError(msg, 5000);
          return;
        }
        const geo = await validateStoreCheckInGeofence(loc.lat, loc.lng);
        if (!geo.ok) {
          showError(geo.message, 5000);
          return;
        }
        latitude = loc.lat;
        longitude = loc.lng;
      } else {
        try {
          const loc = await getUserLocation();
          latitude = loc.lat;
          longitude = loc.lng;
        } catch {
          /* optional coords for check-out */
        }
      }

      const res = await markAttendance(latitude, longitude);
      if (res?.success === false) {
        const msg = res.message || 'Failed. Please try again.';
        showError(msg, 5000);
        return;
      }
      const wasCheckedIn = isCheckedIn === true;
      await fetchProfile({ showLoading: false });
      const resolved = !wasCheckedIn;
      setIsCheckedIn(resolved);
      onPartnerAvailabilityChange?.(resolved);
      persistAttendanceAction(resolved ? 'in' : 'out');
      showSuccess(resolved ? 'Check-in successful!' : 'Check-out successful!', 3000);
    } catch (err: any) {
      const msg =
        (typeof err === 'string' ? err : null) ||
        err?.message ||
        err?.detail ||
        (Array.isArray(err?.non_field_errors) ? err.non_field_errors.join(' ') : null) ||
        'Failed. Please try again.';
      showError(String(msg), 5000);
    } finally {
      setIsMarking(false);
    }
  };

  if (!isOpen) return null;

  const u = profile?.user as Record<string, unknown> | undefined;
  const phone =
    (typeof u?.phone === 'string' && u.phone) ||
    (typeof profile?.phone === 'string' && profile.phone) ||
    '—';
  const email =
    (typeof u?.email === 'string' && u.email.trim()) ? u.email : '—';
  const storeName = (typeof profile?.store_name === 'string' && profile.store_name) || '—';
  const vehicle =
    [profile?.vehicle_type, profile?.vehicle_number].filter(Boolean).join(' · ') || '—';
  const totalDel =
    profile?.total_deliveries != null ? String(profile.total_deliveries) : '—';
  const todayDel =
    profile?.today_deliveries != null ? String(profile.today_deliveries) : '—';
  const pendingDel =
    profile?.pending_deliveries != null ? String(profile.pending_deliveries) : '—';
  const dutyLabel =
    isCheckedIn === true ? 'On duty (checked in)' : isCheckedIn === false ? 'Off duty' : '—';

  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose}>
      <div
        className="absolute right-0 top-0 h-[100dvh] max-h-[100dvh] w-[85%] max-w-sm bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-orange-500 text-white px-4 py-3 flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-semibold">Menu</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-white/20">
            <XIcon size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {isProfileLoading ? (
            <div
              className="flex flex-col flex-1 items-center justify-center min-h-[min(100dvh-14rem,360px)] px-6 py-12 text-center border-b border-gray-100 bg-amber-50/30"
              aria-busy="true"
              aria-live="polite"
            >
              <Spinner size={MAIN_SPINNER_SIZE} />
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Your account
                </p>
                <MetaRow icon={UserIcon} label="Name" value={profile?.employeeName} />
                <MetaRow icon={Phone} label="Phone" value={phone} />
                {/* <MetaRow icon={Mail} label="Email" value={email} /> */}
                <MetaRow icon={Store} label="Store" value={storeName} />
                <MetaRow icon={Bike} label="Vehicle" value={vehicle} />
                {/* <MetaRow icon={ClipboardList} label="Deliveries (total · today · pending)" value={`${totalDel} · ${todayDel} · ${pendingDel}`} />
                <MetaRow icon={Activity} label="Duty" value={dutyLabel} /> */}
              </div>

              <div className="p-4 border-b border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Support
                </p>
                <button
                  type="button"
                  onClick={handleCallManager}
                  disabled={String(managerPhone).replace(/\D/g, '').length < 10}
                  className="w-full flex items-center justify-between gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 hover:bg-amber-100 active:scale-[0.99] transition-all disabled:opacity-50 disabled:pointer-events-none"
                >
                  <div className="flex items-center gap-3 text-left min-w-0">
                    <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
                      <HelpCircleIcon size={20} className="text-amber-700" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-amber-900">Call delivery manager</p>
                      <p className="text-sm text-amber-700 truncate">{managerPhone}</p>
                    </div>
                  </div>
                  <Phone size={22} className="text-amber-600 flex-shrink-0" />
                </button>
              </div>

              <div className="p-4 space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Attendance
                </p>
                {isCheckedIn !== true ? (
                  <p className="text-xs text-gray-500 mb-2">
                    Check in within {STORE_CHECK_IN_RADIUS_METERS} m of your store
                    to confirm basket orders.
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={handleCheckInOut}
                  disabled={isMarking}
                  className={`w-full flex flex-row items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl font-medium transition-all disabled:opacity-60 disabled:pointer-events-none ${
                    isCheckedIn
                      ? 'bg-red-50 text-red-700 border-2 border-red-200 hover:bg-red-100'
                      : 'bg-emerald-50 text-emerald-700 border-2 border-emerald-200 hover:bg-emerald-100'
                  }`}
                >
                  {isMarking ? (
                    <>
                      <Loader2 size={20} className="animate-spin flex-shrink-0" aria-hidden />
                      <span>Please wait…</span>
                    </>
                  ) : isCheckedIn ? (
                    <>
                      <LogOutIcon size={20} strokeWidth={2.5} className="flex-shrink-0" />
                      <span>Check out</span>
                    </>
                  ) : (
                    <>
                      <LogInIcon size={20} strokeWidth={2.5} className="flex-shrink-0" />
                      <span>Check In</span>
                    </>
                  )}
                </button>
              </div>

              {/* Reset password — disabled per product request
              <div className="px-4 pt-2 pb-4">
                <button type="button" ...>Reset password</button>
              </div>
              */}
            </>
          )}
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2.5 py-3 px-4 l font-medium bg-transparent text-red-600 "
          >
            <LogOutIcon size={20} strokeWidth={2.5} />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default SideDrawer;
